/**
 * SpriteAvatar — CPU-only replacement for NeuralAvatar's live Wav2Lip video
 * stream. See Docs/NEURAL_AVATAR_CPU_FRIENDLY_LIPSYNC_OPTIONS.md.
 *
 * Instead of decoding a 25fps JPEG stream generated by a live neural-net
 * forward pass per frame (what NeuralAvatar did), this fetches a small,
 * pre-baked set of 18 static images once (9 visemes x open/closed eyes —
 * see backend/app/services/avatar/sprite_avatar.py) and just swaps which
 * <img> is shown, driven by a real audio-derived viseme timeline from
 * Rhubarb Lip Sync. No canvas, no per-frame decoding, no compositing math —
 * the backend already baked full composited frames, so rendering is a
 * single <img src> swap.
 *
 * Same imperative handle (NeuralAvatarHandle) and WebSocket endpoint
 * (/ws/neural-avatar) as before, so this is a drop-in replacement in
 * NeuralAvatarStream.tsx — the backend protocol on that socket changed
 * (visemes + audio instead of a JPEG stream), but the component contract
 * facing the rest of the app did not.
 */

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Box, Typography } from '@mui/material';
import { API_BASE_URL } from '../../config/api';
import type { NeuralAvatarHandle, NeuralAvatarProps } from './avatarTypes';

// ─── Types ────────────────────────────────────────────────────────────────────

type VisemeCode = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'X';

interface SpriteManifest {
  status: string;
  base_url: string;
  sprites: Record<VisemeCode, { open: string; closed: string }>;
  has_blink: boolean;
}

interface VisemeCue {
  start: number; // seconds
  end: number;
  value: VisemeCode;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const API_BASE = API_BASE_URL;
const WS_URL = API_BASE.replace(/^http/, 'ws') + '/ws/neural-avatar';
const SPRITES_URL = `${API_BASE}/avatar/sprites`;

const REST_VISEME: VisemeCode = 'X';
const BLINK_INTERVAL_MS = 3500; // matches the server's blink_interval_s in customizer.py
const BLINK_DURATION_MS = 180;
const PING_INTERVAL = 20_000;

function absoluteSpriteUrl(path: string): string {
  // manifest URLs are like "/uploads/avatar/sprites/viseme_A_open.jpg" —
  // API_BASE_URL already ends in e.g. ".../api/v1"; strip that and use origin.
  const origin = API_BASE.replace(/\/api\/v1\/?$/, '');
  return `${origin}${path}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const SpriteAvatar = forwardRef<NeuralAvatarHandle, NeuralAvatarProps>(
  // agentTalking is accepted for NeuralAvatarProps compatibility but unused
  // here — mouth movement is driven entirely by the real viseme timeline
  // received per sendText() call, not by this coarse talking/not-talking hint.
  ({ active, width, height }, ref) => {
    const [manifest, setManifest] = useState<SpriteManifest | null>(null);
    const [manifestState, setManifestState] = useState<'loading' | 'ready' | 'unavailable'>('loading');
    const [currentViseme, setCurrentViseme] = useState<VisemeCode>(REST_VISEME);
    const [blinkClosed, setBlinkClosed] = useState(false);
    const [wsStatus, setWsStatus] = useState<'idle' | 'connecting' | 'ready' | 'error'>('idle');

    const wsRef = useRef<WebSocket | null>(null);
    const pingRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const visemeTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
    const blinkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const audioCtxRef = useRef<AudioContext | null>(null);
    const audioDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);

    // ── Fetch the sprite manifest once when we become active ─────────────────
    useEffect(() => {
      if (!active) {
        setManifest(null);
        setManifestState('loading');
        return;
      }
      let cancelled = false;
      fetch(SPRITES_URL)
        .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((data: SpriteManifest) => {
          if (cancelled) return;
          setManifest(data);
          setManifestState('ready');
          // Preload every sprite so the first swap has zero latency/flicker.
          Object.values(data.sprites).forEach(({ open, closed }) => {
            const img1 = new Image(); img1.src = absoluteSpriteUrl(open);
            const img2 = new Image(); img2.src = absoluteSpriteUrl(closed);
          });
        })
        .catch(() => {
          if (!cancelled) setManifestState('unavailable');
        });
      return () => { cancelled = true; };
    }, [active]);

    // ── Idle blink loop — independent of visemes, runs whenever we have sprites ──
    useEffect(() => {
      if (!manifest?.has_blink) return;
      blinkIntervalRef.current = setInterval(() => {
        setBlinkClosed(true);
        setTimeout(() => setBlinkClosed(false), BLINK_DURATION_MS);
      }, BLINK_INTERVAL_MS);
      return () => {
        if (blinkIntervalRef.current) clearInterval(blinkIntervalRef.current);
      };
    }, [manifest?.has_blink]);

    // ── Drive the mouth-shape timeline against a real viseme cue list ────────
    const clearVisemeTimers = () => {
      visemeTimersRef.current.forEach(clearTimeout);
      visemeTimersRef.current = [];
    };

    const playVisemeTimeline = (cues: VisemeCue[]) => {
      clearVisemeTimers();
      for (const cue of cues) {
        const t = setTimeout(() => setCurrentViseme(cue.value), cue.start * 1000);
        visemeTimersRef.current.push(t);
      }
      const lastEnd = cues.length ? Math.max(...cues.map(c => c.end)) : 0;
      const restTimer = setTimeout(() => setCurrentViseme(REST_VISEME), lastEnd * 1000);
      visemeTimersRef.current.push(restTimer);
    };

    // ── Audio playback (same decode/route pattern NeuralAvatar used) ─────────
    const playAudioFrame = async (wav: ArrayBuffer) => {
      const ctx = audioCtxRef.current;
      const dest = audioDestRef.current;
      if (!ctx || !dest) return;
      try {
        const decoded = await ctx.decodeAudioData(wav.slice(0));
        const src = ctx.createBufferSource();
        src.buffer = decoded;
        src.connect(ctx.destination);
        src.connect(dest);
        src.start();
      } catch {
        // Malformed WAV frame — ignore, lips just won't move for this reply
      }
    };

    // ── Imperative handle ─────────────────────────────────────────────────────
    useImperativeHandle(ref, () => ({
      sendPcm(pcm: ArrayBuffer) {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(pcm);
        }
      },
      sendText(text: string) {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'tts_text', text }));
        }
      },
      getAudioStream(): MediaStream | null {
        return audioDestRef.current?.stream ?? null;
      },
    }));

    // ── WebSocket lifecycle ───────────────────────────────────────────────────
    useEffect(() => {
      if (!active) {
        setWsStatus('idle');
        clearVisemeTimers();
        setCurrentViseme(REST_VISEME);
        if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
        wsRef.current?.close();
        wsRef.current = null;
        audioCtxRef.current?.close().catch(() => {});
        audioCtxRef.current = null;
        audioDestRef.current = null;
        return;
      }

      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = new AudioCtx();
      audioCtxRef.current = ctx;
      audioDestRef.current = ctx.createMediaStreamDestination();

      setWsStatus('connecting');
      const ws = new WebSocket(WS_URL);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      ws.onopen = () => {
        setWsStatus('ready');
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL);
      };

      ws.onmessage = async (ev: MessageEvent) => {
        // Binary: 0xFE 0xED-prefixed WAV audio frame (same framing as before)
        if (ev.data instanceof ArrayBuffer) {
          const buf = ev.data;
          const header = new Uint8Array(buf, 0, 2);
          if (header[0] === 0xFE && header[1] === 0xED) {
            await playAudioFrame(buf.slice(2));
          }
          return;
        }
        // Text: JSON control message — {"type":"visemes","visemes":[...]}  etc.
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === 'visemes' && Array.isArray(msg.visemes)) {
            playVisemeTimeline(msg.visemes);
          }
        } catch {
          // Non-JSON text frame — ignore
        }
      };

      ws.onerror = () => setWsStatus('error');

      ws.onclose = () => {
        if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
        if (wsRef.current === ws) {
          setWsStatus('idle');
          wsRef.current = null;
        }
      };

      return () => {
        if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
        ws.close();
        wsRef.current = null;
        clearVisemeTimers();
        ctx.close().catch(() => {});
        audioCtxRef.current = null;
        audioDestRef.current = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    // ─────────────────────────────────────────────────────────────────────────

    const spriteEntry = manifest?.sprites?.[currentViseme];
    const showBlinkClosed = blinkClosed && !!spriteEntry?.closed;
    const imgSrc = spriteEntry
      ? absoluteSpriteUrl(showBlinkClosed ? spriteEntry.closed : spriteEntry.open)
      : manifest
      ? absoluteSpriteUrl(manifest.base_url)
      : null;

    return (
      <Box
        sx={{
          position: 'relative',
          width,
          height,
          bgcolor: '#0a0a18',
          borderRadius: 1,
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {imgSrc && (
          <Box
            component="img"
            src={imgSrc}
            alt="Avatar"
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}

        {manifestState !== 'ready' && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                bgcolor: 'rgba(31,147,255,0.1)',
                border: '2px solid rgba(31,147,255,0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: manifestState === 'loading' ? 'hub-pulse 1.4s ease-out infinite' : 'none',
              }}
            >
              <Box component="span" sx={{ fontSize: 24, lineHeight: 1 }}>🤖</Box>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: manifestState === 'unavailable' ? 'error.main' : 'grey.600',
                fontSize: '0.6rem',
                letterSpacing: '0.1em',
                fontWeight: 600,
              }}
            >
              {manifestState === 'loading' ? 'LOADING…' : 'NO PREVIEW'}
            </Typography>
          </Box>
        )}
      </Box>
    );
  },
);

SpriteAvatar.displayName = 'SpriteAvatar';
